import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { SITE_URL } from '@/components/SEO';
import { useTechnologyData } from '@/hooks/useTranslatedData';
import { useTranslation } from 'react-i18next';

const techSchema = {
  '@type': 'WebPage',
  name: '核心技术 | 膳兜兜 foodgogo',
  description: '膳兜兜foodgogo四大核心技术：FD冻干技术、微囊靶向技术、低温冷萃技术、生物酶解技术，让实验室黑科技成果走进日常饮食。',
  url: `${SITE_URL}/technology`,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'FD冻干技术',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '微囊靶向技术',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: '低温冷萃技术',
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: '生物酶解技术',
      },
    ],
  },
};

export default function Technology() {
  const { t } = useTranslation('common');
  const data = useTechnologyData();

  return (
    <div className="min-h-screen">
      <SEO
        title="核心技术 | 膳兜兜 foodgogo"
        description="膳兜兜foodgogo四大核心技术：FD冻干技术、微囊靶向技术、低温冷萃技术、生物酶解技术，让实验室黑科技成果走进日常饮食。"
        keywords="膳兜兜,冻干技术,微囊靶向,低温冷萃,生物酶解,食品科技,健康食品技术"
        schema={techSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden">
          {/* KV Background */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20scientific%20visualization%20of%20molecular%20structures%20and%20data%20networks%20soft%20mint%20green%20and%20deep%20charcoal%20tones%20floating%20translucent%20particles%20and%20geometric%20patterns%20modern%20laboratory%20technology%20aesthetic%20gentle%20gradient%20lighting%20clean%20minimal%20composition%20premium%20biotechnology%20brand%20visual%20no%20text&width=1920&height=600&seq=tech-hero-kv-001&orientation=landscape')`
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-brand-cream/95 via-brand-cream/88 to-brand-cream/95" />

          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold tracking-wider text-brand-gray uppercase mb-4">
              {data.hero.subtitle}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-brand-charcoal mb-6 leading-tight">
              {data.hero.title}
            </h1>
            <p className="text-base md:text-lg text-brand-gray leading-relaxed">
              {data.hero.description}
            </p>
          </div>
        </section>

        {/* Lab Stats */}
        <section className="py-10 md:py-14 bg-brand-charcoal">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {data.labStats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <span className="block text-3xl md:text-4xl font-bold text-brand-mint mb-1">
                    {stat.value}
                  </span>
                  <span className="text-sm text-brand-gray-light">{stat.label}</span>
                  <span className="text-xs text-white/40 ml-1">{stat.unit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-16 md:py-24 bg-white">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="space-y-16 md:space-y-24">
              {data.technologies.map((tech, index) => (
                <div key={tech.name} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="rounded-2xl overflow-hidden mb-6">
                      <img
                        src={[
                          'https://readdy.ai/api/search-image?query=Close-up%20of%20industrial%20freeze%20drying%20equipment%20in%20modern%20food%20laboratory%2C%20frost%20crystals%20forming%20on%20glass%20surfaces%2C%20scientific%20equipment%20with%20gauges%20and%20dials%2C%20cold%20blue%20and%20white%20lighting%2C%20professional%20technology%20photography%2C%20shallow%20depth%20of%20field&width=600&height=400&seq=60&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=Microscopic%20view%20of%20spherical%20microcapsule%20particles%20suspended%20in%20liquid%2C%20scientific%20visualization%20style%2C%20soft%20warm%20lighting%2C%20translucent%20amber%20and%20coral%20colored%20spheres%2C%20modern%20biotechnology%20aesthetic%2C%20professional%20science%20photography&width=600&height=400&seq=61&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=Modern%20cold%20extraction%20equipment%20with%20glass%20tubes%20and%20condensers%20in%20food%20laboratory%2C%20botanical%20extracts%20dripping%20slowly%2C%20warm%20amber%20lighting%2C%20clean%20scientific%20workspace%2C%20green%20plant%20ingredients%20nearby%2C%20professional%20editorial%20photography&width=600&height=400&seq=62&orientation=landscape',
                          'https://readdy.ai/api/search-image?query=Scientist%20pipetting%20enzyme%20solution%20into%20test%20tubes%20in%20modern%20laboratory%2C%20colorful%20reagents%20and%20glassware%2C%20clean%20white%20workspace%2C%20soft%20natural%20lighting%2C%20biotechnology%20research%20aesthetic%2C%20professional%20science%20photography%2C%20green%20and%20amber%20accents&width=600&height=400&seq=63&orientation=landscape',
                        ][index]}
                        alt={tech.name}
                        className="w-full h-64 md:h-72 object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <span className="inline-block text-xs font-semibold tracking-wider text-brand-gray uppercase mb-2">
                      {tech.shortName}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold text-brand-charcoal mb-4">
                      {tech.name}
                    </h2>
                    <div className="flex items-baseline gap-3 mb-6">
                      <span className="text-4xl md:text-5xl font-bold text-brand-mint-dark">
                        {tech.stat}
                      </span>
                      <span className="text-sm text-brand-gray">{tech.statDesc}</span>
                    </div>
                    <p className="text-base text-brand-gray leading-relaxed mb-6">
                      {tech.description}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {tech.benefits.map((b) => (
                        <div key={b} className="flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-check-line text-brand-mint-dark text-sm"></i>
                          </div>
                          <span className="text-sm text-brand-gray">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-24 bg-brand-sand">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-3">
                {data.process.title}
              </h2>
              <p className="text-base text-brand-gray">
                {data.process.subtitle}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {data.process.steps.map((step) => (
                <div key={step.step} className="bg-white rounded-2xl p-6 md:p-8 relative overflow-hidden">
                  <span className="absolute -top-2 -right-2 text-7xl font-bold text-brand-mint/10 select-none">
                    {step.step}
                  </span>
                  <span className="inline-block text-sm font-bold text-brand-mint-dark mb-3">
                    {t('misc.step')} {step.step}
                  </span>
                  <h3 className="text-base font-semibold text-brand-charcoal mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-brand-gray leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}